import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, AlertCircle } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import { useI18n } from '../../hooks/useI18n';


interface Category {
    id: string;
    name: string;
    internal_id?: string;
    description?: string;
    parent_id?: string;
    is_active?: boolean;
    created_at: string;
    updated_at?: string;
}

interface EditForm {
    name: string;
    internal_id: string;
    description: string;
}

interface CreateForm {
    name: string;
    internal_id: string;
    description: string;
    parent_id: string | null;
}

interface ImportPreviewRow {
    row_number: number;
    row_type: 'base' | 'translation';
    internal_id: string;
    category_name?: string;
    language_code?: string;
    action: string;
    status: 'valid' | 'error';
    message?: string;
}

interface ImportRowIssue {
    row_number: number;
    message: string;
}

interface ImportPreviewResponse {
    category_type: 'cost' | 'revenue';
    import_session_token: string;
    summary: {
        total_rows: number;
        base_rows: number;
        translation_rows: number;
        to_create: number;
        to_update: number;
        translation_creates: number;
        translation_updates: number;
        errors: number;
    };
    rows: ImportPreviewRow[];
    row_issues: ImportRowIssue[];
}

interface TreeNodeProps {
    category: Category;
    level?: number;
    isChild?: boolean;
    childCategories: Record<string, Category[]>;
    expandedNodes: Set<string>;
    editingNode: string | null;
    editForm: EditForm;
    refreshKey: number;
    toggleNode: (id: string) => void;
    startEdit: (category: Category) => void;
    startCreate: (parentId?: string) => void;
    deleteCategory: (id: string) => void;
    saveEdit: () => void;
    cancelEdit: () => void;
    handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleInternalIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

// TreeNode component moved outside to prevent recreation
const TreeNode: React.FC<TreeNodeProps> = ({
    category,
    level = 0,
    isChild = false,
    childCategories,
    expandedNodes,
    editingNode,
    editForm,
    refreshKey,
    toggleNode,
    startEdit,
    startCreate,
    deleteCategory,
    saveEdit,
    cancelEdit,
    handleNameChange,
    handleInternalIdChange,
    handleDescriptionChange
}) => {
    const { t } = useI18n();
    const hasChildren = childCategories[category.id]?.length > 0;
    const isExpanded = expandedNodes.has(category.id);
    const isEditing = editingNode === category.id;


    const compareCategory = (a: Category, b: Category) => {
        const aHas = !!a.internal_id && a.internal_id.trim() !== '';
        const bHas = !!b.internal_id && b.internal_id.trim() !== '';
        if (aHas && bHas) return a.internal_id!.localeCompare(b.internal_id!);
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;
        return a.name.localeCompare(b.name);
    };

    const childrenForThisNode = useMemo(() => {
        if (!hasChildren) return [] as Category[];
        const arr = childCategories[category.id] || [];
        // Sort only second-level categories (children of root nodes)
        if (level === 0) return [...arr].sort(compareCategory);
        return arr;
    }, [hasChildren, childCategories, category.id, level, refreshKey]);

    return (
        <div className={`${level > 0 ? 'ml-6' : ''}`}>
            <div className="flex items-center group hover:bg-gray-50 rounded-md p-2">
                {/* Expand/Collapse Button */}
                <div className="w-6 h-6 flex items-center justify-center mr-2">
                    {hasChildren ? (
                        <button
                            onClick={() => toggleNode(category.id)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <ChevronRight
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            />
                        </button>
                    ) : (
                        <div className="w-4 h-4" />
                    )}
                </div>

                {/* Category Content */}
                <div className="flex-1 min-w-0">
                    {isEditing ? (
                        <div className="space-y-2">
                            <Input
                                key={`name-${category.id}`}
                                value={editForm.name}
                                onChange={handleNameChange}
                                placeholder={t('forms.categoryName', 'common')}
                                className="text-sm"
                                autoFocus
                            />
                            <Input
                                key={`internal_id-${category.id}`}
                                value={editForm.internal_id}
                                onChange={handleInternalIdChange}
                                placeholder={t('categories.internalIdPlaceholder', 'common')}
                                className="text-sm"
                            />
                            <textarea
                                key={`description-${category.id}`}
                                value={editForm.description}
                                onChange={handleDescriptionChange}
                                placeholder={t('forms.descriptionOptional', 'common')}
                                rows={2}
                                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            />
                            <div className="flex gap-2">
                                <Button size="sm" onClick={saveEdit}>{t('actions.save', 'common')}</Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit}>{t('actions.cancel', 'common')}</Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                    <h4 className={`font-medium text-foreground truncate ${isChild ? 'text-sm' : 'text-base'}`}>
                                        {category.name}
                                        {category.internal_id && (
                                            <span className="ml-2 text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                                                {category.internal_id}
                                            </span>
                                        )}
                                    </h4>
                                    {category.description && (
                                        <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => startEdit(category)}
                                        className="text-xs"
                                    >
                                        {t('actions.edit', 'common')}
                                    </Button>
                                    {level < 2 && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startCreate(category.id)}
                                            className="text-xs"
                                        >
                                                {t('categories.addSubcategory', 'common')}
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => deleteCategory(category.id)}
                                        className="text-xs text-destructive hover:text-destructive/90"
                                    >
                                        {t('actions.delete', 'common')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Child Categories */}
            {hasChildren && isExpanded && (
                <div className="ml-4 border-l border-border">
                    {childrenForThisNode.map((child) => (
                        <TreeNode
                            key={`${child.id}-${refreshKey}`}
                            category={child}
                            level={level + 1}
                            isChild={true}
                            childCategories={childCategories}
                            expandedNodes={expandedNodes}
                            editingNode={editingNode}
                            editForm={editForm}
                            refreshKey={refreshKey}
                            toggleNode={toggleNode}
                            startEdit={startEdit}
                            startCreate={startCreate}
                            deleteCategory={deleteCategory}
                            saveEdit={saveEdit}
                            cancelEdit={cancelEdit}
                            handleNameChange={handleNameChange}
                            handleInternalIdChange={handleInternalIdChange}
                            handleDescriptionChange={handleDescriptionChange}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function CategoriesPage() {
    const [activeTab, setActiveTab] = useState<'cost' | 'revenue'>('cost');
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [editingNode, setEditingNode] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<EditForm>({ name: '', internal_id: '', description: '' });
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [createForm, setCreateForm] = useState<CreateForm>({ name: '', internal_id: '', description: '', parent_id: null });
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [canImport, setCanImport] = useState<boolean>(false);
    const [importReason, setImportReason] = useState<string>('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importPreview, setImportPreview] = useState<ImportPreviewResponse | null>(null);
    const [isPreviewingImport, setIsPreviewingImport] = useState<boolean>(false);
    const [isCommittingImport, setIsCommittingImport] = useState<boolean>(false);
    const importFileInputRef = useRef<HTMLInputElement | null>(null);
    const { t } = useI18n();

    const fetchImportAvailability = useCallback(async () => {
        try {
            const endpoint = activeTab === 'cost'
                ? '/api/categories/import/availability'
                : '/api/revenue-categories/import/availability';
            const response = await apiFetch(endpoint, { method: 'GET', skipRedirect: true } as RequestInit & { skipRedirect: boolean });
            if (!response.ok) {
                setCanImport(false);
                setImportReason('');
                return;
            }
            const data = await response.json();
            setCanImport(!!data.can_import);
            setImportReason(data.reason || '');
        } catch {
            setCanImport(false);
            setImportReason('');
        }
    }, [activeTab]);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = activeTab === 'cost' ? '/api/categories/' : '/api/revenue-categories/';
            const response = await apiFetch(endpoint);
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
                // Auto-expand all parent nodes initially
                const parentIds = data.filter((cat: Category) => !cat.parent_id).map((cat: Category) => cat.id);
                setExpandedNodes(new Set(parentIds));
            }
            await fetchImportAvailability();
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, fetchImportAvailability]);

    useEffect(() => {
        fetchCategories();
        setImportFile(null);
        setImportPreview(null);
    }, [fetchCategories, activeTab]);

    // Helper functions
    const toggleNode = useCallback((nodeId: string) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    }, [expandedNodes]);

    const startEdit = useCallback((category: Category) => {
        setEditingNode(category.id);
        setEditForm({
            name: category.name,
            internal_id: category.internal_id || '',
            description: category.description || ''
        });
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingNode(null);
        setEditForm({ name: '', internal_id: '', description: '' });
    }, []);

    // Memoized onChange handlers to prevent unnecessary re-renders
    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEditForm(prev => ({ ...prev, name: e.target.value }));
    }, []);

    const handleInternalIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEditForm(prev => ({ ...prev, internal_id: e.target.value }));
    }, []);

    const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditForm(prev => ({ ...prev, description: e.target.value }));
    }, []);

    const saveEdit = useCallback(async () => {
        if (!editingNode) return;

        // Clear any previous error messages
        setErrorMessage('');

        try {
            const endpoint = activeTab === 'cost' ? `/api/categories/${editingNode}` : `/api/revenue-categories/${editingNode}`;
            const response = await apiFetch(endpoint, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editForm)
            });

            if (response.ok) {
                await fetchCategories();
                setEditingNode(null);
                setEditForm({ name: '', internal_id: '', description: '' });
                setRefreshKey(prev => prev + 1); // Force re-render
            } else {
                // Extract error message from response
                const errorData = await response.json();
                const message = errorData.detail || t('categories.updateFailed', 'common');
                setErrorMessage(message);
            }
        } catch (error) {
            console.error('Error updating category:', error);
            setErrorMessage(t('categories.updateUnexpected', 'common'));
        }
    }, [editingNode, editForm, fetchCategories, activeTab, t]);

    const startCreate = useCallback((parentId: string | null = null) => {
        setIsCreating(true);
        setCreateForm({ name: '', internal_id: '', description: '', parent_id: parentId });
    }, []);

    const cancelCreate = () => {
        setIsCreating(false);
        setCreateForm({ name: '', internal_id: '', description: '', parent_id: null });
    };

    const saveCreate = async () => {
        // Clear any previous error messages
        setErrorMessage('');

        try {
            const endpoint = activeTab === 'cost' ? '/api/categories/' : '/api/revenue-categories/';
            const response = await apiFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(createForm)
            });

            if (response.ok) {
                await fetchCategories();
                setIsCreating(false);
                setCreateForm({ name: '', internal_id: '', description: '', parent_id: null });
            } else {
                // Extract error message from response
                const errorData = await response.json();
                const message = errorData.detail || t('categories.createFailed', 'common');
                setErrorMessage(message);
            }
        } catch (error) {
            console.error('Error creating category:', error);
            setErrorMessage(t('categories.createUnexpected', 'common'));
        }
    };

    const deleteCategory = useCallback(async (categoryId: string) => {
        if (!confirm(t('categories.deleteConfirm', 'common'))) return;

        // Clear any previous error messages
        setErrorMessage('');

        try {
            const endpoint = activeTab === 'cost' ? `/api/categories/${categoryId}` : `/api/revenue-categories/${categoryId}`;
            const response = await apiFetch(endpoint, {
                method: 'DELETE'
            });

            if (response.ok) {
                await fetchCategories();
            } else {
                // Extract error message from response
                const errorData = await response.json();
                const message = errorData.detail || t('categories.deleteFailed', 'common');
                setErrorMessage(message);
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            setErrorMessage(t('categories.deleteUnexpected', 'common'));
        }
    }, [fetchCategories, activeTab, t]);

    const downloadImportTemplate = useCallback(async () => {
        try {
            const endpoint = activeTab === 'cost'
                ? '/api/categories/import/template'
                : '/api/revenue-categories/import/template';
            const response = await apiFetch(endpoint);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setErrorMessage(errorData.detail || t('categories.import.errors.downloadTemplateFailed', 'common'));
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = activeTab === 'cost'
                ? 'cost-categories-import-template.xlsx'
                : 'revenue-categories-import-template.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading template:', error);
            setErrorMessage(t('categories.import.errors.downloadTemplateFailed', 'common'));
        }
    }, [activeTab, t]);

    const previewImportFile = useCallback(async (file: File) => {
        setIsPreviewingImport(true);
        setErrorMessage('');
        try {
            const formData = new FormData();
            formData.append('file', file);
            const endpoint = activeTab === 'cost'
                ? '/api/categories/import/preview'
                : '/api/revenue-categories/import/preview';
            const response = await apiFetch(endpoint, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setErrorMessage(errorData.detail || t('categories.import.errors.previewFailed', 'common'));
                return;
            }

            const data = await response.json();
            setImportPreview(data);
        } catch (error) {
            console.error('Error previewing import:', error);
            setErrorMessage(t('categories.import.errors.previewUnexpected', 'common'));
        } finally {
            setIsPreviewingImport(false);
        }
    }, [activeTab, t]);

    const commitImport = useCallback(async () => {
        if (!importFile || !importPreview?.import_session_token) {
            setErrorMessage(t('categories.import.errors.runPreviewFirst', 'common'));
            return;
        }
        setIsCommittingImport(true);
        setErrorMessage('');
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('import_session_token', importPreview.import_session_token);
            const endpoint = activeTab === 'cost'
                ? '/api/categories/import/commit'
                : '/api/revenue-categories/import/commit';
            const response = await apiFetch(endpoint, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                setErrorMessage(errorData.detail || t('categories.import.errors.commitFailed', 'common'));
                return;
            }

            await fetchCategories();
            setImportPreview(null);
            setImportFile(null);
        } catch (error) {
            console.error('Error committing import:', error);
            setErrorMessage(t('categories.import.errors.commitUnexpected', 'common'));
        } finally {
            setIsCommittingImport(false);
        }
    }, [activeTab, importFile, importPreview, fetchCategories, t]);

    // Group categories by parent - memoized to prevent unnecessary re-renders
    const parentCategories = useMemo(() => {
        const roots = categories.filter((cat: Category) => !cat.parent_id);
        roots.sort((a: Category, b: Category) => {
            const aHas = !!a.internal_id && a.internal_id.trim() !== '';
            const bHas = !!b.internal_id && b.internal_id.trim() !== '';
            if (aHas && bHas) return a.internal_id!.localeCompare(b.internal_id!);
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            return a.name.localeCompare(b.name);
        });
        return roots;
    }, [categories]);

    const childCategories = useMemo(() =>
        categories.reduce((acc: Record<string, Category[]>, cat: Category) => {
            if (cat.parent_id) {
                if (!acc[cat.parent_id]) {
                    acc[cat.parent_id] = [];
                }
                acc[cat.parent_id].push(cat);
            }
            return acc;
        }, {}),
        [categories]
    );



    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            {t('categories.title', 'common')}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {t('categories.subtitle', 'common')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canImport && (
                            <>
                                <Button variant="outline" onClick={downloadImportTemplate}>
                                    {t('categories.import.downloadTemplate', 'common')}
                                </Button>
                            </>
                        )}
                        <Button onClick={() => startCreate()}>
                            {activeTab === 'cost'
                                ? t('categories.addCostCategory', 'common')
                                : t('categories.addRevenueCategory', 'common')}
                        </Button>
                    </div>
                </div>

                {canImport && (
                    <div className="rounded-lg border bg-card p-4 space-y-3">
                        <div className="text-sm text-muted-foreground">
                            {t('categories.import.initialSetupHint', 'common')}
                        </div>
                        <input
                            ref={importFileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            onClick={(e) => {
                                // Reset so selecting the same file triggers onChange again.
                                (e.currentTarget as HTMLInputElement).value = '';
                            }}
                            onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                setImportFile(file);
                                setImportPreview(null);
                                if (file) {
                                    previewImportFile(file);
                                }
                            }}
                        />
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => importFileInputRef.current?.click()}
                                disabled={isPreviewingImport}
                            >
                                {isPreviewingImport
                                    ? t('categories.import.previewing', 'common')
                                    : t('categories.import.chooseImportFile', 'common')}
                            </Button>
                            <input
                                readOnly
                                value={importFile?.name || ''}
                                className="flex-1 text-sm text-muted-foreground bg-muted/40 rounded px-3 py-2 border"
                                placeholder={t('categories.import.noFileSelected', 'common')}
                            />
                        </div>
                    </div>
                )}

                {!canImport && importReason && (
                    <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                        {importReason}
                    </div>
                )}

                {importPreview && (
                    <div className="rounded-lg border bg-card p-4 space-y-4">
                        <h3 className="text-lg font-semibold">{t('categories.import.previewTitle', 'common')}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                            <div>{t('categories.import.totalRows', 'common')}: {importPreview.summary.total_rows}</div>
                            <div>{t('categories.import.createCount', 'common')}: {importPreview.summary.to_create}</div>
                            <div>{t('categories.import.updateCount', 'common')}: {importPreview.summary.to_update}</div>
                            <div>{t('categories.import.errorCount', 'common')}: {importPreview.summary.errors}</div>
                        </div>

                        {importPreview.row_issues.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-destructive">{t('categories.import.rowIssues', 'common')}</p>
                                <div className="max-h-40 overflow-auto border rounded p-2 text-sm">
                                    {importPreview.row_issues.map((issue, idx) => (
                                        <div key={`${issue.row_number}-${idx}`} className="text-destructive">
                                            {t('categories.import.rowLabel', 'common')} {issue.row_number}: {issue.message}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="max-h-48 overflow-auto border rounded">
                            <table className="w-full text-sm">
                                <thead className="bg-muted">
                                    <tr>
                                        <th className="text-left p-2">{t('categories.import.table.row', 'common')}</th>
                                        <th className="text-left p-2">{t('categories.import.table.type', 'common')}</th>
                                        <th className="text-left p-2">{t('categories.import.table.name', 'common')}</th>
                                        <th className="text-left p-2">{t('categories.import.table.internalId', 'common')}</th>
                                        <th className="text-left p-2">{t('categories.import.table.action', 'common')}</th>
                                        <th className="text-left p-2">{t('categories.import.table.status', 'common')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {importPreview.rows.map((row) => (
                                        <tr key={`${row.row_number}-${row.row_type}-${row.internal_id}`} className="border-t">
                                            <td className="p-2">{row.row_number}</td>
                                            <td className="p-2">{row.row_type}</td>
                                            <td className="p-2">{row.category_name || '-'}</td>
                                            <td className="p-2">{row.internal_id}</td>
                                            <td className="p-2">{row.action}</td>
                                            <td className={`p-2 ${row.status === 'error' ? 'text-destructive' : 'text-green-600'}`}>
                                                {row.status}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                onClick={commitImport}
                                disabled={isCommittingImport || importPreview.summary.errors > 0}
                            >
                                {isCommittingImport
                                    ? t('categories.import.importing', 'common')
                                    : t('categories.import.commitImport', 'common')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setImportPreview(null);
                                    setImportFile(null);
                                    if (importFileInputRef.current) {
                                        importFileInputRef.current.value = '';
                                    }
                                }}
                            >
                                {t('categories.import.clearPreview', 'common')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-2 border-b">
                    <button
                        onClick={() => setActiveTab('cost')}
                        className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                            activeTab === 'cost'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {t('categories.costCategories', 'common')}
                    </button>
                    <button
                        onClick={() => setActiveTab('revenue')}
                        className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                            activeTab === 'revenue'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {t('categories.revenueCategories', 'common')}
                    </button>
                </div>

                {/* Error Message */}
                {errorMessage && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                )}

                {/* Create Form */}
                {isCreating && (
                    <div className="rounded-lg border bg-card p-6">
                        <h3 className="text-lg font-semibold mb-4">
                            {createForm.parent_id
                                ? t('categories.addSubcategory', 'common')
                                : t('categories.addCategory', 'common')}
                        </h3>
                        <div className="space-y-4">
                            <Input
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                placeholder={t('forms.categoryName', 'common')}
                            />
                            <Input
                                value={createForm.internal_id}
                                onChange={(e) => setCreateForm({ ...createForm, internal_id: e.target.value })}
                                placeholder={t('categories.internalIdPlaceholder', 'common')}
                            />
                            <textarea
                                value={createForm.description}
                                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                                placeholder={t('forms.descriptionOptional', 'common')}
                                rows={3}
                                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            />
                            <div className="flex gap-2">
                                <Button onClick={saveCreate}>{t('actions.create', 'common') }</Button>
                                <Button variant="outline" onClick={cancelCreate}>{t('actions.cancel', 'common')}</Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Categories Tree */}
                <div>
                    {loading ? (
                        <div className="p-8 text-center">
                            <div className="flex items-center justify-center space-x-2">
                                <Skeleton className="h-4 w-4 rounded-full" />
                                <Skeleton className="h-4 w-[100px]" />
                            </div>
                            <p className="mt-2 text-muted-foreground">
                                {t('categories.loading', 'common')}
                            </p>
                        </div>
                    ) : (
                        <div>
                            {parentCategories.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-muted-foreground">
                                        {t('categories.noCategories', 'common')}
                                    </p>
                                    <Button
                                        onClick={() => startCreate()}
                                        className="mt-4"
                                    >
                                        {t('categories.createFirstCategory', 'common')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {parentCategories.map((parent) => (
                                        <TreeNode
                                            key={`${parent.id}-${refreshKey}`}
                                            category={parent}
                                            childCategories={childCategories}
                                            expandedNodes={expandedNodes}
                                            editingNode={editingNode}
                                            editForm={editForm}
                                            refreshKey={refreshKey}
                                            toggleNode={toggleNode}
                                            startEdit={startEdit}
                                            startCreate={startCreate}
                                            deleteCategory={deleteCategory}
                                            saveEdit={saveEdit}
                                            cancelEdit={cancelEdit}
                                            handleNameChange={handleNameChange}
                                            handleInternalIdChange={handleInternalIdChange}
                                            handleDescriptionChange={handleDescriptionChange}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/categories')({
  component: CategoriesPage,
})
